<template>
  <div id="easy-returns">
    <div id="easy-returns-title" class="h1 cell border-bottom padding-bottom-m medium-padding-bottom-xl" v-text="config.EASY_RETURN.title"></div>
    <div class="cell margin-top-m margin-bottom-l medium-margin-top-xl">
      <div id="easy-returns-step1" class="h2">
        <img src="/assets/img/order-history.svg" alt="order history" class="order-history-icon" />
        <span v-text="config.EASY_RETURN.step_one.title"></span>
      </div>
    </div>
    <div class="cell grid-x margin-bottom-xl">
      <div class="small-12 medium-4 border medium-padding-right-xxl">
        <div v-if="!isSignedIn" class="h3">Sign In</div>
        <div v-else class="h3">Look up an order</div>
        <div class="large margin-top-s">
          <span v-if="!isSignedIn">Search for your order to start a return.</span>
          <span v-else>View your order history to start your return.</span>
          <span v-if="isMcom && !isSignedIn">You can ship your returns for free when you join Star Rewards.</span>
        </div>
        <button id="start-return" class="primary button expanded margin-top-s margin-bottom-m" @click="startReturn">View My Orders</button>
      </div>
      <div class="small-12 medium-8 medium-padding-left-xxl">
        <Notification v-if="orderDetailsStore.orderDetailsError || showLockoutMessage" severity="error" :closable="false" class="margin-bottom-m">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="easy-return-error" v-html="notificationMessage"></div>
        </Notification>
        <div
          class="h3 margin-bottom-s medium-margin-top-z"
          :class="{ 'small-margin-top-m': !(orderDetailsStore.orderDetailsError || showLockoutMessage) }"
        >
          Or, enter order number
        </div>
        <div class="grid-x">
          <div class="small-12 medium-10 large margin-bottom-s">
            Look up your order using the number provided at the top of your order confirmation email or invoice.
          </div>
          <div class="small-12 medium-6">
            <Form class="gift-return-search-form" @submit="onSubmit">
              <div class="margin-bottom-l">
                <InputText
                  v-model="orderNumber"
                  name="orderNumber"
                  label="Order Number (Required)"
                  :rule="validateOrderNumber"
                  @keypress="restrictOrderNumber($event)"
                />
              </div>
              <div class="margin-bottom-l">
                <p class="large margin-bottom-s">Was this order a gift? (Required)</p>
                <Field v-model="isGift" name="isGift" type="radio" class="is-gift" value="yes" /> Yes
                <Field v-model="isGift" name="isGift" type="radio" class="is-gift margin-left-xxs" value="no" /> No
              </div>
              <div v-if="isGift === 'no'" class="margin-bottom-l">
                <p class="large margin-bottom-s">Verify with your email:</p>
                <InputText
                  v-model="emailAddress"
                  name="emailAddress"
                  label="Email Address"
                  :rule="emailRule"
                  :disabled="!!phoneNumber"
                  @on-clear="clearField($event, 'emailAddress')"
                />
              </div>
              <div v-if="isGift === 'no'" class="margin-bottom-l">
                <p class="large margin-bottom-s">Or, verify with you phone number:</p>
                <InputText
                  v-model="phoneNumber"
                  name="phoneNumber"
                  label="Phone Number"
                  type="tel"
                  :rule="phoneRule"
                  mask="phoneFormat"
                  :disabled="!!emailAddress"
                  @keypress="digitsOnly($event)"
                  @on-clear="clearField($event, 'phoneNumber')"
                />
              </div>
              <div v-if="isGift === 'yes'" class="margin-bottom-l">
                <p class="large margin-bottom-s">Enter Shipping Address Zip Code:</p>
                <InputText v-model="zipCode" name="zipCode" label="Zip code" :rule="zipCodeRule" @on-clear="clearField($event, 'zipCode')" />
              </div>
              <AppButton class="primary expanded" :loading="orderDetailsStore.isLoading"> View Order Details </AppButton>
            </Form>
          </div>
        </div>
      </div>
    </div>
    <div class="cell grid-x border-top margin-bottom-xl">
      <div class="small-12 medium-4 margin-top-l medium-padding-right-xl">
        <div id="easy-returns-step2" class="h2 margin-bottom-s">
          <img src="/assets/img/print.svg" alt="order history" class="order-history-icon" />
          <span v-text="config.EASY_RETURN.step_two.title"></span>
        </div>
        <p class="large" v-text="config.EASY_RETURN.step_two.text"></p>
      </div>
      <div class="small-12 medium-6 small-margin-top-m medium-margin-top-l medium-padding-left-xl">
        <div id="easy-returns-step3" class="h2 margin-bottom-s">
          <img src="/assets/img/order-history.svg" alt="order history" class="order-history-icon" />
          <span v-text="config.EASY_RETURN.step_three.title"></span>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -->
        <p class="large" v-html="config.EASY_RETURN.step_three.text"></p>
      </div>
    </div>
    <div class="cell border-top margin-bottom-xl medium-margin-bottom-xxxl">
      <h2 id="easy-returns-step4" class="small-margin-top-m medium-margin-top-l margin-bottom-s" v-text="config.EASY_RETURN.step_four.title"></h2>
      <!-- eslint-disable-next-line vue/no-v-html -->
      <p class="large" v-html="config.EASY_RETURN.step_four.text"></p>
    </div>
    <p class="small hide-for-small-only" v-text="config.EASY_RETURN.note"></p>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import Form from '@atomic-ui/form';
import Notification from '@nuxt-common/atomic-ui/app/components/notification/Notification';
import InputText from '@nuxt-common/atomic-ui/app/components/inputText/InputText';
import { veevalidate } from '@nuxt-common/util/app/utils/forms';
import { UserStatus } from '@nuxt-common/util/app/utils/cookie';
import { fireTag } from '@nuxt-common/util/app/utils/tagManager';

// Local components
import AppButton from '@/components/AppButton';
import { orderLookupFailedAttemptsConfig } from '@/utils/OrderLookupFailedAttempts';
import { digitsOnly, validateOrderNumber } from '@/utils/General';
import useBrand from '@/composables/useBrand';

// Config
import * as config from 'config';

// Pinia Store & Composables
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
const orderDetailsStore = useOrderDetailsStore();
const { isMcom } = useBrand();

const { Field } = veevalidate;

// Reactive state
const orderNumber = ref('');
const showLockoutMessage = ref(false);
const zipCode = ref('');
const emailAddress = ref('');
const phoneNumber = ref('');
const isGift = ref('no');
const emailRule = ref('');
const phoneRule = ref('');
const zipCodeRule = ref('');
const submitted = ref(false);

const isSignedIn = UserStatus.isSignedIn();

// Computed properties
const notificationMessage = computed(() =>
  orderDetailsStore.orderDetailsError && orderDetailsStore.orderDetailsError !== ''
    ? orderDetailsStore.orderDetailsError
    : orderLookupFailedAttemptsConfig.ORDER_LOOKUP_FAILED_ATTEMPTS_MSG,
);

// Methods
const startReturn = () => {
  fireTag('link', {
    event_name: 'view orders',
    custom_link_name: 'easy return_view my orders',
  });
  window.location.href = isSignedIn ? '/purchases/online' : '/account/signin?fromPage=easyReturn';
};

const clearField = (value, field) => {
  [field].value = value;
};

const restrictOrderNumber = (e) => {
  const currentLength = orderNumber.value.length;
  if (currentLength < 20) return true;
  return e.preventDefault();
};

const onSubmit = async (values) => {
  fireTag('link', {
    event_name: 'view orders details',
    custom_link_name: 'easy return_view order details',
  });

  const payloadObj = {
    orderNumber: values.orderNumber,
    fromLookup: 'returns',
    isSignedIn,
    payload: {
      isGiftOption: values.isGift === 'yes',
      zipCode: values.zipCode || '',
      email: values.emailAddress || '',
      phoneNumber: values.phoneNumber || '',
    },
  };

  await orderDetailsStore.GET_ONLINE_ORDER_DETAILS({ payload: payloadObj, router: window.location });
  submitted.value = false;
};

// Lifecycle hooks
onMounted(() => {
  if (orderLookupFailedAttemptsConfig.isOrderLookupFailedAttempts()) {
    showLockoutMessage.value = true;
  }
  fireTag('view', {
    page_name: 'easy return',
    page_type: 'return center',
  });
});
</script>
<style lang="scss" scoped>
.border {
  border-bottom: $gray-3-border;
  @include breakpoint(medium up) {
    border-right: $gray-3-border;
    border-bottom: 0;
  }
}
</style>
